import type { ReactNode } from 'react'
import type { OrderDetails } from '@/entities/order/model'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { orderKeys } from '@/entities/order/model/order.queryKeys'
import { useStateConfigQuery } from '@/entities/state/model'
import { useIsRealtimeAuthSessionAvailable } from '@/features/auth/model'
import { useConnectionStatusStore } from '@/shared/lib/connection-status'
import { useRealtimeStore } from '../model/realtime.store'
import { useRealtimeConnectionMonitor } from './useRealtimeConnectionMonitor'

/**
 * IMPLEMENTATION NOTES:
 *
 * This provider implements a dual-mode data fetching strategy:
 * 1. HTTP Polling (fallback): Always available, provides immediate data on mount
 * 2. WebSocket Realtime (preferred): Connects asynchronously, takes over when ready
 *
 * The immediate sync (syncOrders(false)) eliminates the 10-second delay issue
 * where users would see no data while waiting for WebSocket connection.
 *
 * Connection flow:
 * 1. Component mounts → Immediate HTTP sync loads data via syncOrders(false)
 * 2. WebSocket connects in background (with WebView delay if needed)
 * 3. Once connected, realtime updates take over from polling
 * 4. If WebSocket fails, polling continues to provide data
 *
 * This ensures users always see data quickly, regardless of network conditions.
 */

/**
 * Props for RealtimeProvider component
 * @property children - Child components that will have access to realtime data
 */
interface RealtimeProviderProps {
  children: ReactNode
}

/**
 * RealtimeProvider - Orchestrates WebSocket connections and realtime data synchronization
 *
 * Responsibilities:
 * - Initializes realtime store and WebSocket connection management
 * - Triggers immediate HTTP sync on mount to eliminate initial load delay
 * - Controls WebSocket connection based on authentication and feature flags
 * - Manages query invalidation callbacks for seamless realtime-to-polling transitions
 * - Subscribes to realtime events and triggers appropriate data refreshes
 * - Monitors connection status and provides telemetry
 *
 * This provider should wrap the application at a high level, after authentication
 * is established but before any components that consume realtime data.
 */
/**
 * Custom hook to manage realtime store subscriptions. Handles wait time refresh when
 * orders change and state config refetch when realtime events occur.
 */
function useRealtimeSubscriptions(
  onStateUpdate: () => void,
  onWaitTimeRefresh: (orders: OrderDetails[]) => void,
) {
  useEffect(() => {
    const unsubscribeOrders = useRealtimeStore.subscribe(
      state => state.realtimeOrders,
      (orders) => {
        const hasWaitTime = orders.some(order => order.waitTime !== undefined)
        if (hasWaitTime) {
          onWaitTimeRefresh(orders)
        }
      },
    )

    const unsubscribeEvents = useRealtimeStore.subscribe(
      state => state.lastStateUpdateTime,
      onStateUpdate,
    )

    return () => {
      unsubscribeOrders()
      unsubscribeEvents()
    }
  }, [onStateUpdate, onWaitTimeRefresh])
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { refetch: refetchStateConfig, data: stateConfig } = useStateConfigQuery()
  const { initialise, setShouldConnect, setInvalidateQueriesCallback, setInvalidateStateUpdateCallback, setInvalidateWaitTimeCallback, syncOrders } = useRealtimeStore()
  const { setRealtimeEnabled } = useConnectionStatusStore()
  const isAuthed = useIsRealtimeAuthSessionAvailable()
  const queryClient = useQueryClient()

  const realtimeEnabled = stateConfig?.features?.realtimeUpdates ?? false

  // Callback: Invalidate order queries when switching from realtime to polling
  const invalidateOrderQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: orderKeys.orders() })
  }, [queryClient])

  // Callback: Invalidate wait time queries when orders with waitTime are received via WebSocket
  const invalidateWaitTimeQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: orderKeys.waitTime() })
  }, [queryClient])

  // Effect: Register query invalidation callbacks for realtime-to-polling transitions and wait time updates
  useEffect(() => {
    setInvalidateQueriesCallback(invalidateOrderQueries)
    setInvalidateStateUpdateCallback(refetchStateConfig)
    setInvalidateWaitTimeCallback(invalidateWaitTimeQueries)
  }, [setInvalidateQueriesCallback, setInvalidateStateUpdateCallback, setInvalidateWaitTimeCallback, invalidateOrderQueries, refetchStateConfig, invalidateWaitTimeQueries])

  // Effect: Subscribe to realtime store changes
  useRealtimeSubscriptions(refetchStateConfig, invalidateWaitTimeQueries)

  // Effect: Initialise realtime store on mount - Sets up WebSocket connection management and state
  useEffect(() => {
    initialise()
  }, [initialise])

  // Effect: Trigger immediate HTTP sync on mount via syncOrders(false) - Loads orders via HTTP without waiting for WebSocket connection.
  // Errors are caught and logged but don't prevent app initialization since polling provides fallback.
  useEffect(() => {
    syncOrders(false).catch((error) => {
      console.error('[RealtimeProvider] Immediate sync failed:', error)
    })
  }, [syncOrders])

  // Effect: Control WebSocket connection based on authentication and feature flag.
  // Connects when user is authenticated AND realtime feature is enabled. Disconnects when either condition becomes false.
  useEffect(() => {
    if (!isAuthed) {
      setShouldConnect(false)
      return
    }

    setShouldConnect(realtimeEnabled)
  }, [realtimeEnabled, setShouldConnect, isAuthed])

  // Effect: Guarantee disconnect when provider unmounts
  useEffect(() => {
    return () => {
      setShouldConnect(false)
    }
  }, [setShouldConnect])

  // Effect: Sync realtime feature flag to connection status store - Updates global state for polling control
  useEffect(() => {
    setRealtimeEnabled(realtimeEnabled)
  }, [realtimeEnabled, setRealtimeEnabled])

  useRealtimeConnectionMonitor()

  return <>{children}</>
}
